/**
 * CAL-001 · Blackout confirmation dialog.
 *
 * Named actions per spec: **Go back** (safe default, writes nothing)
 * and **Schedule anyway** (invokes the override path). Never renders
 * a generic weekend warning — only shows when the server or the
 * frontend has actually matched a UK public holiday or a manual
 * blackout period.
 *
 * Consumers pass:
 *   • open         — modal visibility.
 *   • matches[]    — server response `matches` from the 409 body
 *                    (also accepted client-side from listBlackouts()).
 *   • date         — the primary calendar date being scheduled (ISO
 *                    yyyy-MM-dd) — rendered in the dialog title.
 *   • onCancel()   — Go back.
 *   • onConfirm()  — Schedule anyway.
 */
import React from "react";
import { AlertTriangle, X } from "lucide-react";

function formatUkDate(iso) {
    if (!iso) return "";
    try {
        const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function BlackoutConfirmationDialog({ open, matches = [], date, onCancel, onConfirm }) {
    if (!open) return null;
    const rows = Array.isArray(matches) ? matches : [];
    const primary = rows[0] || {};
    const heading = date || primary.date || "";
    const isPublic = (m) => m.source === "uk_public_holiday";
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="blackout-dialog-title"
            data-testid="blackout-dialog"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
        >
            <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-[#E5E7EB]">
                <div className="flex items-start gap-3 p-5 border-b border-[#F3F4F6]">
                    <div className="w-9 h-9 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                        <div id="blackout-dialog-title" className="text-[15px] font-semibold text-[#131B2F]">
                            Confirm scheduling on a blackout date
                        </div>
                        <div className="mt-1 text-sm text-[#4B5565]" data-testid="blackout-dialog-date">
                            {formatUkDate(heading)}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="p-1 rounded hover:bg-[#F5F5F7] text-[#4B5565]"
                    >
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-5 space-y-3" data-testid="blackout-dialog-matches">
                    <p className="text-sm text-[#131B2F]">
                        This date overlaps {rows.length === 1 ? "the following blackout" : "the following blackouts"}.
                        Do you still want to schedule this work?
                    </p>
                    <ul className="space-y-2">
                        {rows.map((m, i) => (
                            <li
                                key={`${m.source}-${m.date}-${i}`}
                                data-testid={`blackout-match-${i}`}
                                className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-sm"
                            >
                                <div className="font-medium text-[#131B2F]">{m.name || "Blackout"}</div>
                                <div className="text-xs text-[#4B5565]">
                                    {formatUkDate(m.date)} · {isPublic(m) ? "UK public holiday" : "Manual blackout"}
                                    {m.region ? ` · ${m.region}` : ""}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="flex items-center justify-end gap-2 p-4 border-t border-[#F3F4F6] bg-[#FAFAFA] rounded-b-2xl">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="blackout-dialog-cancel"
                        className="h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Go back
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        data-testid="blackout-dialog-confirm"
                        className="h-9 px-3 rounded-md bg-[#B45309] text-white text-sm font-medium hover:bg-[#92400E]"
                    >
                        Schedule anyway
                    </button>
                </div>
            </div>
        </div>
    );
}
